/**
 * Collection Filters Component
 * Handles mobile filter drawer, filter application, and AJAX updates
 * Uses event delegation so handlers work after AJAX DOM replacement
 */
(function() {
  'use strict';

  // Filter drawer state management
  const FilterDrawer = {
    isOpen: false,
    focusTrap: null,
    lastFocusedElement: null,

    getElements: function() {
      return {
        wrapper: document.querySelector('collection-filters'),
        toggle: document.querySelector('[data-filter-toggle]'),
        close: document.querySelector('[data-filter-close]'),
        overlay: document.querySelector('[data-filter-overlay]'),
        panel: document.querySelector('[data-all-collection-filters]')
      };
    },

    init: function() {
      // Use event delegation - attach to document once
      document.addEventListener('click', this.handleClick.bind(this));
      document.addEventListener('keydown', this.handleKeydown.bind(this));
    },

    handleClick: function(e) {
      const target = e.target;

      // Open button clicked
      if (target.closest('[data-filter-toggle]')) {
        e.preventDefault();
        this.open();
        return;
      }

      // Close button clicked
      if (target.closest('[data-filter-close]')) {
        e.preventDefault();
        this.close();
        return;
      }

      // Overlay clicked
      if (target.closest('[data-filter-overlay]')) {
        e.preventDefault();
        this.close();
        return;
      }
    },

    handleKeydown: function(e) {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    },

    open: function() {
      const elements = this.getElements();
      if (!elements.wrapper) return;

      this.isOpen = true;
      this.lastFocusedElement = document.activeElement;

      // Add classes for styling and scroll lock
      elements.wrapper.classList.add('filters-open');
      document.body.classList.add('filters-open');

      // Store scroll position and lock body
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      // Update accessibility attributes
      if (elements.toggle) {
        elements.toggle.setAttribute('aria-expanded', 'true');
      }
      if (elements.panel) {
        elements.panel.setAttribute('aria-hidden', 'false');
        elements.panel.setAttribute('role', 'dialog');
        elements.panel.setAttribute('aria-modal', 'true');
        elements.panel.setAttribute('aria-label', 'Filter and sort products');

        // Set initial focus to close button or first interactive element
        setTimeout(() => {
          const closeBtn = elements.panel.querySelector('[data-filter-close]');
          if (closeBtn) {
            closeBtn.focus();
          } else {
            const firstFocusable = elements.panel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            if (firstFocusable) firstFocusable.focus();
          }
        }, 100);

        // Initialize focus trap
        this.initFocusTrap(elements.panel);
      }
    },

    close: function() {
      const elements = this.getElements();
      if (!elements.wrapper) return;

      this.isOpen = false;

      // Remove classes
      elements.wrapper.classList.remove('filters-open');
      document.body.classList.remove('filters-open');

      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY) * -1);
      }

      // Update accessibility attributes
      if (elements.toggle) {
        elements.toggle.setAttribute('aria-expanded', 'false');
      }
      if (elements.panel) {
        elements.panel.setAttribute('aria-hidden', 'true');
        elements.panel.removeAttribute('role');
        elements.panel.removeAttribute('aria-modal');
      }

      // Remove focus trap
      this.removeFocusTrap();

      // Restore focus to last focused element
      if (this.lastFocusedElement) {
        this.lastFocusedElement.focus();
        this.lastFocusedElement = null;
      }
    },

    initFocusTrap: function(container) {
      this.focusTrap = (e) => {
        const focusableElements = container.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];

        if (e.key === 'Tab') {
          if (e.shiftKey) {
            if (document.activeElement === firstFocusable) {
              e.preventDefault();
              lastFocusable.focus();
            }
          } else {
            if (document.activeElement === lastFocusable) {
              e.preventDefault();
              firstFocusable.focus();
            }
          }
        }
      };

      document.addEventListener('keydown', this.focusTrap);
    },

    removeFocusTrap: function() {
      if (this.focusTrap) {
        document.removeEventListener('keydown', this.focusTrap);
        this.focusTrap = null;
      }
    }
  };

  // Price range functionality
  const PriceRange = {
    update: function() {
      const sliderMin = document.getElementById('Filter-filter.v.price.gte');
      const sliderMax = document.getElementById('Filter-filter.v.price.lte');
      const minDisplay = document.getElementById('price-range-min-value');
      const maxDisplay = document.getElementById('price-range-max-value');
      const selection = document.querySelector('.slider-selection');

      if (!sliderMin || !sliderMax || !minDisplay || !maxDisplay || !selection) return;

      let minVal = parseInt(sliderMin.value);
      let maxVal = parseInt(sliderMax.value);

      // Swap if min > max
      if (minVal > maxVal) {
        sliderMin.value = maxVal;
        sliderMax.value = minVal;
        [minVal, maxVal] = [maxVal, minVal];
      }

      minDisplay.textContent = minVal;
      maxDisplay.textContent = maxVal;

      // Update slider track
      const minPercent = (minVal / sliderMax.max) * 100;
      const maxPercent = (maxVal / sliderMax.max) * 100;
      selection.style.left = `${minPercent}%`;
      selection.style.right = `${100 - maxPercent}%`;
    }
  };

  // URL and filter management
  const FilterManager = {
    init: function() {
      // Use event delegation for filter changes
      document.addEventListener('change', this.handleChange.bind(this));
      document.addEventListener('input', this.handleInput.bind(this));
    },

    handleInput: function(e) {
      // Only handle price range inputs
      if (e.target.matches('input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]')) {
        PriceRange.update();
      }
    },

    handleChange: function(event) {
      const target = event.target;

      // Check if this is within the main render area
      const mainRender = document.querySelector('[data-main-collection-render], [data-main-search-render]');
      if (!mainRender || !mainRender.contains(target)) return;

      const isSortSelect = target.matches('collection-sort select');
      const isFilter = target.matches(
        '[data-all-collection-filters] input, [data-all-collection-filters] select'
      );
      const isPriceInput = target.matches(
        'input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]'
      );

      if (!isSortSelect && !isFilter && !isPriceInput) return;

      // Close the filter drawer on mobile when a filter is applied
      if (isFilter && window.innerWidth < 1024) {
        FilterDrawer.close();
      }

      const currentUrl = new URL(window.location);
      const newUrl = new URL(currentUrl);
      const searchParams = new URLSearchParams(currentUrl.search);

      // Remove page parameter when any filter is applied
      searchParams.delete('page');

      if (isFilter && !isPriceInput) {
        if ((target.type === 'checkbox' || target.type === 'radio') && target.checked) {
          searchParams.append(target.name, target.value);
        } else {
          const pathWithoutFilter = target.getAttribute('data-url-to-remove');
          if (pathWithoutFilter) {
            history.replaceState({ path: pathWithoutFilter }, '', pathWithoutFilter);
            this.renderSection();
            return;
          }
        }
      }

      if (isSortSelect) {
        searchParams.set('sort_by', target.value);
      }

      if (isPriceInput) {
        const minInput = document.querySelector('input[name="filter.v.price.gte"]');
        const maxInput = document.querySelector('input[name="filter.v.price.lte"]');

        if (minInput && minInput.value) {
          searchParams.set('filter.v.price.gte', minInput.value);
        } else {
          searchParams.delete('filter.v.price.gte');
        }

        if (maxInput && maxInput.value) {
          searchParams.set('filter.v.price.lte', maxInput.value);
        } else {
          searchParams.delete('filter.v.price.lte');
        }
      }

      newUrl.search = searchParams.toString();
      if (newUrl.toString() !== currentUrl.toString()) {
        history.replaceState({ path: newUrl.toString() }, '', newUrl.toString());
        this.renderSection();
      }
    },

    renderSection: function() {
      const container = document.getElementById('ProductGridContainer');
      const productGrid = document.getElementById('product-grid');

      if (productGrid) {
        productGrid.classList.add('loading');
      }

      let fetchUrl = '';
      let sectionParam = '';

      if (window.location.pathname.includes('/collections/')) {
        fetchUrl = `${window.location.pathname}${window.location.search}`;
        sectionParam = 'section_id=main-collection-product-grid';
      } else if (window.location.pathname.includes('/search')) {
        fetchUrl = `${window.location.pathname}${window.location.search}`;
        sectionParam = 'section_id=main-search';
      }

      fetchUrl += window.location.search.includes('?')
        ? `&${sectionParam}`
        : `?${sectionParam}`;

      fetch(fetchUrl)
        .then((response) => response.text())
        .then((html) => {
          const newContent = new DOMParser().parseFromString(html, 'text/html');
          const newSection = newContent.querySelector('#ProductGridContainer');

          if (newSection && container && container.parentNode) {
            container.parentNode.replaceChild(newSection, container);
          }
        })
        .catch((error) => {
          console.error('Error fetching updated content:', error);
        })
        .finally(() => {
          setTimeout(() => {
            const grid = document.getElementById('product-grid');
            if (grid) {
              grid.classList.remove('loading');
            }
            PriceRange.update();
          }, 800);
        });
    }
  };

  // Initialize on DOM ready
  document.addEventListener('DOMContentLoaded', function() {
    FilterDrawer.init();
    FilterManager.init();
    PriceRange.update();
  });

  // Expose for potential external use / debugging
  window.CollectionFilters = {
    drawer: FilterDrawer,
    manager: FilterManager
  };

})();
